import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type { CommonReportFilters } from "../types";

const REPORTS_KEY = ["reports"] as const;

function cleanFilters(
  filters: CommonReportFilters,
): Record<string, string | number | undefined> {
  const out: Record<string, string | number | undefined> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = v as string | number;
  }
  return out;
}

function useReportQuery<T>(
  reportId: string,
  endpoint: string,
  filters: CommonReportFilters,
  enabled: boolean,
): UseQueryResult<T> {
  const { accessToken } = useAuth();
  return useQuery<T>({
    queryKey: [...REPORTS_KEY, reportId, filters, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: () =>
      apiFetch<T>(endpoint, {
        token: accessToken,
        query: cleanFilters(filters),
      }),
  });
}

// Project reports
export function useProjectHealthReport(filters: CommonReportFilters, enabled: boolean) {
  return useReportQuery<{ data: unknown[] }>(
    "project-health",
    "/reports/project-health",
    filters,
    enabled,
  );
}

export function useOverdueItemsReport(filters: CommonReportFilters, enabled: boolean) {
  return useReportQuery<{
    tasks: unknown[];
    actions: unknown[];
    milestones: unknown[];
    summary: {
      totalOverdue: number;
      taskCount: number;
      actionCount: number;
      milestoneCount: number;
      criticalCount: number;
    };
  }>("overdue-items", "/reports/overdue-items", filters, enabled);
}

export function useProjectTimelineReport(filters: CommonReportFilters, enabled: boolean) {
  return useReportQuery<{ data: unknown[] }>(
    "project-timeline",
    "/reports/project-timeline",
    filters,
    enabled,
  );
}

// Finance reports
export function useBudgetVsActualReport(filters: CommonReportFilters, enabled: boolean) {
  return useReportQuery<{
    data: unknown[];
    summary: {
      totalBudget: number;
      totalSpent: number;
      totalRemaining: number;
      avgUtilization: number;
      overBudgetCount: number;
    };
  }>("budget-vs-actual", "/reports/budget-vs-actual", filters, enabled);
}

export function useExpenseAnalysisReport(filters: CommonReportFilters, enabled: boolean) {
  return useReportQuery<unknown>(
    "expense-analysis",
    "/reports/expense-analysis",
    filters,
    enabled,
  );
}

export function useProfitabilityReport(filters: CommonReportFilters, enabled: boolean) {
  return useReportQuery<{ data: unknown[]; summary: unknown }>(
    "profitability",
    "/reports/profitability",
    filters,
    enabled,
  );
}

// People reports
export function useTeamWorkloadReport(filters: CommonReportFilters, enabled: boolean) {
  return useReportQuery<{ data: unknown[] }>(
    "team-workload",
    "/reports/team-workload",
    filters,
    enabled,
  );
}

export function useActivitySummaryReport(filters: CommonReportFilters, enabled: boolean) {
  return useReportQuery<{ data: unknown[] }>(
    "activity-summary",
    "/reports/activity-summary",
    filters,
    enabled,
  );
}

export function useMeetingAttendanceReport(filters: CommonReportFilters, enabled: boolean) {
  return useReportQuery<{ data: unknown[]; summary: unknown }>(
    "meeting-attendance",
    "/reports/meeting-attendance",
    filters,
    enabled,
  );
}

// Work reports
export function useTasksStatusReport(filters: CommonReportFilters, enabled: boolean) {
  return useReportQuery<unknown>(
    "tasks-status",
    "/reports/tasks-status",
    filters,
    enabled,
  );
}

export function useActionsTrackerReport(filters: CommonReportFilters, enabled: boolean) {
  return useReportQuery<unknown>(
    "actions-tracker",
    "/reports/actions-tracker",
    filters,
    enabled,
  );
}

export function useDecisionsLogReport(filters: CommonReportFilters, enabled: boolean) {
  return useReportQuery<{ data: unknown[]; summary: unknown }>(
    "decisions-log",
    "/reports/decisions-log",
    filters,
    enabled,
  );
}

export function useIdeasPipelineReport(filters: CommonReportFilters, enabled: boolean) {
  return useReportQuery<unknown>(
    "ideas-pipeline",
    "/reports/ideas-pipeline",
    filters,
    enabled,
  );
}

// Export utility
export interface ReportExportColumn {
  key: string;
  header: string;
  width?: number;
  type?: "text" | "number" | "currency" | "date" | "badge";
}

export interface ReportExportPayload {
  reportId: string;
  title: string;
  columns: ReportExportColumn[];
  data: Array<Record<string, unknown>>;
  filters?: string;
  format: "csv" | "excel" | "pdf";
  orientation?: "portrait" | "landscape";
}

const API_BASE = (
  import.meta.env.VITE_API_URL ?? 
  (import.meta.env.DEV ? "http://localhost:3000/api" : "/api")
).replace(/\/$/, "");

export async function exportReport(
  payload: ReportExportPayload,
  accessToken: string | null,
): Promise<void> {
  const res = await fetch(`${API_BASE}/reports/export`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let msg = `Export failed (${res.status})`;
    try {
      const json = await res.json();
      if (json && typeof json.error === "string") msg = json.error;
    } catch {
      // ignore
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="?([^"]+)"?/i.exec(cd);
  const filename =
    match?.[1] ??
    `${payload.title.replace(/[^a-z0-9]+/gi, "_")}_${new Date()
      .toISOString()
      .slice(0, 10)}.${payload.format === "excel" ? "xlsx" : payload.format}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
